import React, { useMemo, useState } from "react";
import { Button } from "components/common/Button";
import { Fold } from "components/common/Fold";
import { InfoHint } from "components/common/InfoHint";
import { InlineSpinner } from "components/common/InlineSpinner";
import { PersonPicker, type Person } from "components/common/PersonPicker";
import { SegmentedControl } from "@/components/common/SegmentedControl";
import { formatShortDayMonth } from "@/lib/dates";
import { useMcpStats } from "@/lib/api/billing/org-billing";
import type {
  McpBreakdown,
  McpCaller,
  McpPeriod,
} from "@/lib/api/organizations";

const DAY_SECONDS = 86_400;
const TOP_CALLERS = 3;
const PLOT_HEIGHT = "7rem";
const CALLS = "var(--color-chart-1)";
/// Everyone else's share while one member is picked out.
const BACKDROP = "color-mix(in srgb, var(--color-chart-1) 30%, transparent)";
/// The same hue at the ramp's far end: a part of the calls, not another series.
const FAILED = "var(--color-chart-ramp-5)";
const HATCH =
  "repeating-linear-gradient(45deg, var(--color-border) 0 0.125rem, transparent 0.125rem 0.3125rem)";
const REFUSED: React.CSSProperties = {
  background: "var(--color-error-bg)",
  boxShadow: "inset 0 0 0 1px var(--color-error)",
};

interface Caller {
  clientId: string;
  label: string;
  person: Person;
  charged: Map<string, number>;
  failedOn: Map<string, number>;
  calls: number;
  failed: number;
  refusals: number;
}

const utcDay = (unixSeconds: number) =>
  new Date(unixSeconds * 1000).toISOString().slice(0, 10);

/// A `YYYY-MM-DD` is a UTC day; a local Date built from its parts names the same day.
const dayLabel = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return formatShortDayMonth(new Date(y, m - 1, d));
};

const callerLabel = (caller: McpCaller | undefined) =>
  caller?.name ?? caller?.email ?? "Former member";

const bump = (map: Map<string, number>, key: string, n: number) =>
  map.set(key, (map.get(key) ?? 0) + n);

/// Every caller, most calls first. The picker searches by address, so anyone
/// without one is listed under their label and a short id.
function callersOf(breakdown: McpBreakdown): Caller[] {
  const known = new Map(breakdown.members.map((m) => [m.client_id, m]));
  const byId = new Map<string, Caller>();
  for (const row of breakdown.days) {
    let caller = byId.get(row.client_id);
    if (!caller) {
      const member = known.get(row.client_id);
      const label = callerLabel(member);
      caller = {
        clientId: row.client_id,
        label,
        person: member?.email
          ? { email: member.email, displayName: member.name }
          : { email: `${label} · ${row.client_id.slice(-6)}` },
        charged: new Map(),
        failedOn: new Map(),
        calls: 0,
        failed: 0,
        refusals: 0,
      };
      byId.set(row.client_id, caller);
    }
    bump(caller.charged, row.day, row.charged);
    bump(caller.failedOn, row.day, row.failed);
    caller.calls += row.charged;
    caller.failed += row.failed;
    caller.refusals += row.refused;
  }
  return [...byId.values()].sort(
    (a, b) => b.calls - a.calls || a.label.localeCompare(b.label),
  );
}

const swatch = (background: string) => (
  <i className="inline-block size-2 rounded-app" style={{ background }} />
);

const KEY = (
  <div className="grid grid-cols-[auto_auto_1fr] items-center gap-x-2 gap-y-1">
    {swatch(CALLS)}
    <span className="font-medium">Calls</span>
    <span>Count toward the monthly limit</span>
    {swatch(FAILED)}
    <span className="font-medium">Failed</span>
    <span>Count too: the tool ran and returned an error</span>
    <i className="inline-block size-2 rounded-app" style={REFUSED} />
    <span className="font-medium">Refused</span>
    <span>Turned away at the limit, not counted</span>
    <span className="col-span-3 text-fg-muted pt-1">
      Days are UTC. Calls that fail on our side are refunded.
    </span>
  </div>
);

export const McpUsageBreakdown: React.FC<{ breakdown: McpBreakdown }> = ({
  breakdown,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const model = useMemo(() => {
    const count = Math.max(
      1,
      Math.round((breakdown.period_end - breakdown.period_start) / DAY_SECONDS),
    );
    const days = Array.from({ length: count }, (_, i) =>
      utcDay(breakdown.period_start + i * DAY_SECONDS),
    );
    const today = days.indexOf(utcDay(Date.now() / 1000));
    const lastShown = today === -1 ? days.length - 1 : today;
    const tracked = days.findIndex((d) =>
      breakdown.days.some((row) => row.day === d),
    );

    const charged = new Map<string, number>();
    const failed = new Map<string, number>();
    const refused = new Map<string, number>();
    for (const row of breakdown.days) {
      bump(charged, row.day, row.charged);
      bump(failed, row.day, row.failed);
      bump(refused, row.day, row.refused);
    }
    const totals = days.map((d) => charged.get(d) ?? 0);
    const failures = days.map((d) => failed.get(d) ?? 0);
    const refusals = days.map((d) => refused.get(d) ?? 0);
    return {
      days,
      today,
      lastShown,
      firstTracked: tracked === -1 ? lastShown + 1 : tracked,
      callers: callersOf(breakdown),
      totals,
      failures,
      refusals,
      dayMax: Math.max(1, ...days.map((_, i) => totals[i] + refusals[i])),
    };
  }, [breakdown]);

  const { days, callers } = model;
  const n = days.length;
  // A personal org has one member: a member view would restate the total.
  const byMember = callers.length > 1;
  const selected = callers.find((c) => c.clientId === selectedId) ?? null;
  const ownOn = (i: number) => selected?.charged.get(days[i]) ?? 0;
  const showBand = breakdown.before_tracking > 0 && model.firstTracked > 0;

  const readout = (() => {
    if (hover === null || hover > model.lastShown) return null;
    const day = dayLabel(days[hover]);
    if (hover < model.firstTracked) return `${day} · before tracking`;
    const parts = [`${model.totals[hover].toLocaleString()} calls`];
    if (selected) parts.push(`${selected.label} ${ownOn(hover)}`);
    if (model.failures[hover] > 0)
      parts.push(`${model.failures[hover]} failed`);
    if (model.refusals[hover] > 0)
      parts.push(`${model.refusals[hover]} refused`);
    return `${day} · ${parts.join(" · ")}`;
  })();

  const shown = callers.slice(0, TOP_CALLERS);
  if (selected && !shown.includes(selected)) shown.push(selected);
  const rest = callers.filter((c) => !shown.includes(c));
  const sum = (list: Caller[], key: "calls" | "failed" | "refusals") =>
    list.reduce((s, c) => s + c[key], 0);

  const row = (
    key: string,
    label: React.ReactNode,
    calls: number,
    failed: number | null,
    refusals: number | null,
    extra: React.HTMLAttributes<HTMLTableRowElement> = {},
  ) => (
    <tr key={key} className="border-t border-border" {...extra}>
      <td className="py-1">{label}</td>
      <td className="text-right py-1">{calls.toLocaleString()}</td>
      <td className="text-right py-1">{failed?.toLocaleString() ?? "–"}</td>
      <td className="text-right py-1">{refusals?.toLocaleString() ?? "–"}</td>
    </tr>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="mcp-breakdown">
      {byMember && (
        <PersonPicker
          directory={callers.map((c) => c.person)}
          selected={selected?.person ?? null}
          onSelect={(person) =>
            setSelectedId(
              callers.find((c) => c.person.email === person?.email)?.clientId ??
                null,
            )
          }
          placeholder="Show one member's calls…"
          ariaLabel="Show one member's calls"
        />
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-fg-muted-regular-small">
        <span className="inline-flex items-center gap-1">
          {swatch(selected ? BACKDROP : CALLS)}
          {selected ? "Everyone" : "Calls"}
        </span>
        {selected && (
          <span className="inline-flex items-center gap-1">
            {swatch(CALLS)}
            {selected.label}
          </span>
        )}
        {(selected
          ? selected.failed > 0
          : model.failures.some((f) => f > 0)) && (
          <span className="inline-flex items-center gap-1">
            {swatch(FAILED)}
            Failed
          </span>
        )}
        {model.refusals.some((r) => r > 0) && (
          <span className="inline-flex items-center gap-1">
            <i className="inline-block size-2 rounded-app" style={REFUSED} />
            Refused
          </span>
        )}
        {showBand && (
          <span className="inline-flex items-center gap-1">
            {swatch(HATCH)}
            Before tracking
          </span>
        )}
        <InfoHint title={KEY} label="How MCP calls are counted" />
      </div>

      <div
        className="relative"
        style={{ height: PLOT_HEIGHT }}
        onMouseLeave={() => setHover(null)}
      >
        {showBand && (
          <div
            className="absolute inset-y-0 left-0 flex items-center justify-center overflow-hidden text-fg-muted-regular-small"
            style={{
              width: `${(Math.min(model.firstTracked, n) / n) * 100}%`,
              background: HATCH,
            }}
          >
            <span className="bg-bg px-1 rounded-app whitespace-nowrap">
              {breakdown.before_tracking.toLocaleString()}
            </span>
          </div>
        )}
        <div className="absolute inset-0 flex items-end gap-1 border-b border-border">
          {days.map((day, i) => {
            const highlighted = selected ? ownOn(i) : model.totals[i];
            const failed = Math.min(
              highlighted,
              selected ? (selected.failedOn.get(day) ?? 0) : model.failures[i],
            );
            const others = model.totals[i] - highlighted;
            const height = (v: number) => `${(v / model.dayMax) * 100}%`;
            return (
              <div
                key={day}
                className="flex h-full flex-1 flex-col-reverse"
                onMouseEnter={() => setHover(i)}
                style={{ opacity: hover !== null && hover !== i ? 0.55 : 1 }}
              >
                {failed > 0 && (
                  <div
                    data-testid="mcp-breakdown-failed"
                    style={{ height: height(failed), background: FAILED }}
                  />
                )}
                {highlighted - failed > 0 && (
                  <div
                    data-testid={
                      selected ? "mcp-breakdown-selected" : undefined
                    }
                    style={{
                      height: height(highlighted - failed),
                      background: CALLS,
                    }}
                  />
                )}
                {others > 0 && (
                  <div
                    style={{
                      height: height(others),
                      background: selected ? BACKDROP : CALLS,
                    }}
                  />
                )}
                {model.refusals[i] > 0 && (
                  <div
                    data-testid="mcp-breakdown-refused"
                    style={{ height: height(model.refusals[i]), ...REFUSED }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="relative h-4 text-fg-muted-regular-small">
        {days.map((day, i) =>
          i % 7 === 0 || i === model.today ? (
            <span
              key={day}
              className={`absolute whitespace-nowrap ${i === model.today ? "text-fg font-medium" : ""}`}
              style={{
                left: `${((i + 0.5) / n) * 100}%`,
                transform: i === 0 ? undefined : "translateX(-50%)",
              }}
            >
              {dayLabel(day)}
            </span>
          ) : null,
        )}
      </div>

      <div
        className="text-fg-muted-regular-small min-h-4"
        data-testid="mcp-breakdown-readout"
      >
        {readout}
      </div>

      <table className="w-full tabular-nums">
        <thead>
          <tr className="text-fg-muted-regular-small">
            <th className="text-left font-normal py-1">
              {byMember ? "Member" : ""}
            </th>
            <th className="text-right font-normal py-1">Calls</th>
            <th className="text-right font-normal py-1">Failed</th>
            <th className="text-right font-normal py-1">Refused</th>
          </tr>
        </thead>
        <tbody>
          {byMember &&
            shown.map((c) =>
              row(
                c.clientId,
                <Button
                  variant="quiet-link"
                  size="sm"
                  onClick={() =>
                    setSelectedId(c === selected ? null : c.clientId)
                  }
                  aria-pressed={c === selected}
                >
                  {c.label}
                </Button>,
                c.calls,
                c.failed,
                c.refusals,
                {
                  "data-testid": "mcp-breakdown-row",
                  className: `border-t border-border ${c === selected ? "bg-selected-light" : ""}`,
                } as React.HTMLAttributes<HTMLTableRowElement>,
              ),
            )}
          {byMember &&
            rest.length > 0 &&
            row(
              "rest",
              <span className="text-fg-muted">
                {rest.length} more {rest.length === 1 ? "member" : "members"}
              </span>,
              sum(rest, "calls"),
              sum(rest, "failed"),
              sum(rest, "refusals"),
            )}
          {breakdown.before_tracking > 0 &&
            row(
              "before",
              <span className="inline-flex items-center gap-2">
                {swatch(HATCH)}
                Before tracking
              </span>,
              breakdown.before_tracking,
              null,
              null,
            )}
          {row(
            "total",
            "Total",
            breakdown.used,
            sum(callers, "failed"),
            sum(callers, "refusals"),
            {
              "data-testid": "mcp-breakdown-total",
              className: "border-t border-border font-medium",
            } as React.HTMLAttributes<HTMLTableRowElement>,
          )}
        </tbody>
      </table>
    </div>
  );
};

/// Behind a button, so opening Plan & usage never pays for the breakdown.
export const McpUsageStats: React.FC<{ orgId: string }> = ({ orgId }) => {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<McpPeriod>("current");
  const stats = useMcpStats(orgId, period, open);
  return (
    <div className="flex flex-col gap-2 px-2 pb-2">
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="quiet-link"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {open ? "Hide stats" : "Show stats"}
        </Button>
        {open && (
          <div className="h-6">
            <SegmentedControl<McpPeriod>
              options={[
                { value: "current", label: "This period" },
                { value: "previous", label: "Previous period" },
              ]}
              value={period}
              onChange={setPeriod}
            />
          </div>
        )}
      </div>
      <Fold open={open}>
        {stats.isPending ? (
          <InlineSpinner />
        ) : stats.data ? (
          <div
            style={{
              opacity: stats.isPlaceholderData ? 0.5 : 1,
              transition: "opacity var(--duration-short) var(--ease-move)",
            }}
          >
            <McpUsageBreakdown breakdown={stats.data} />
          </div>
        ) : (
          <span className="text-fg-muted-regular-small">
            The stats could not be loaded.
          </span>
        )}
      </Fold>
    </div>
  );
};
